<template>
  <CommonCatalogModal
    ref="commonModalRef"
    :selectedProduct="selectedProduct"
    :images="clickedProductModalImagesTransform"
    :modalStyle="catalogModalStyle"
    :isOpenSaleProductBtnDisabled="isOpenSaleProductBtnDisabled"
    :isSubmitting="isClickOpenSaleProductBtn"
    mode="add-variant"
    @open-sale="clickedOpenSaleProductBtn"
    @go-to-product="goToProductPage"
    @modal-closed="addProductModalClosed"
  >
    <template #selectable-area>
      <AddCatalogModalSelectableArea
        ref="addCatalogModalSelectableAreaRef"
        :isClickOpenSaleProductBtn="isClickOpenSaleProductBtn"
        :isCustomValue="true"
        :clickedProductModalData="clickedProductModalData"
        :hasClickedProductVariants="hasClickedProductVariants"
        :productId="props.productId"
        @selectedShipmentTemplate="selectedShipmentTemplate = $event"
        @selectedVariants="handleSelectedVariants"
        @closeParentModal="closeModalWithoutDismiss"
      />
    </template>

    <template #bulk-operations>
      <AddCatalogModalBulkOperations
        v-if="hasClickedProductVariants"
        ref="bulkOperationsRef"
        :selectedRowsLength="getSelectedRowsLength"
        :categoryId="selectedProductCategoryId"
        @applySelected="applySelected"
      />
    </template>

    <template #table>
      <Table
        ref="catalogTableRef"
        :variants="selectedVariants"
        :columns="columnsCatalogTable"
        :hasClickedProductVariants="hasClickedProductVariants"
      />
    </template>

    <template #footer>
      <div class="modal-bottom-alert">
        <img src="@/assets/images/icons/info-circle.svg" />
        <p class="modal-bottom-text">
          Ürüne açıklama veya farklı detay ekleme işlemlerini, satışa açtıktan
          sonra <b>Ürün Yönetimi’nden</b> yapabilirsiniz.
        </p>
      </div>
      <Button
        primary
        lg
        style="width: auto"
        :disabled="isOpenSaleProductBtnDisabled"
        @click="clickedOpenSaleProductBtn"
      >
        Ürünü Satışa Aç
      </Button>
    </template>
  </CommonCatalogModal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { Button } from "@n11/so-ui-components";
import CommonCatalogModal from "@/components/common/Modal/CommonCatalogModal.vue";
import AddCatalogModalSelectableArea from "@/views/AddCatalogProduct/partials/AddCatalogModalSelectableArea.vue";
import AddCatalogModalBulkOperations from "@/views/AddCatalogProduct/partials/AddCatalogModalBulkOperations.vue";
import Table from "@/views/AddCatalogProduct/partials/TableForCatalog";
import { columnService } from "@/views/AddCatalogProduct/services/getCatalogColumns";
import { sendOpenProductForSaleEvent, sendProductDetailEvent } from "@/utils/ga4/helper";
import { GA4_EVENT } from "@/utils/ga4/event";
import { HOST_NAMES } from "@/utils/enum/enviromentHosts";

const props = defineProps({
  productId: {
    type: [String, Number],
    required: false,
  },
  categoryId: {
    type: [String, Number],
    required: false,
  },
  productDetail: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  paginatedRows: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const emit = defineEmits([
  "loadingClickedProductId",
  "setIsNotificationShow",
  "openListedProductWarningModal",
]);

const store = useStore();
const columnServiceInit = columnService();
const validate = columnServiceInit.validate;
const columnsCatalogTable = columnServiceInit.columns;

const commonModalRef = ref();
const catalogModalStyle = ref();
const isVariantAddSuccess = ref(false);
const clickedProductModalImages = ref([]);
const selectedProduct = ref(null);
const isClickOpenSaleProductBtn = ref(false);
const clickedProductModalData = ref();
const hasClickedProductVariants = ref(true);
const selectedShipmentTemplate = ref({});
const selectedVariants = ref([]);
const addCatalogModalSelectableAreaRef = ref();
const bulkOperationsRef = ref();
const catalogTableRef = ref();
const isOpenSaleProductBtnDisabled = ref(false);
const successProcessProductImg = ref();
const isSkuExist = ref(false);
const pimsId = ref(null);
const selectedProductCategoryId = ref(null);

const getCatalogModalDeleteRowId = computed(
  () => store.getters["navigation/getCatalogModalDeleteRowId"]
);
const clickedProductModalImagesTransform = computed(() => {
  if (
    !clickedProductModalImages.value ||
    clickedProductModalImages.value.length === 0
  ) {
    return [];
  }
  return clickedProductModalImages.value.map((item) => ({
    Url:
      typeof item?.imageUrl === "string"
        ? item.imageUrl.replace("/org/", "/261_371/")
        : "",
  }));
});

const getSelectedRowsLength = computed(
  () => Object.keys(catalogTableRef.value?.getSelectedRows() ?? []).length
);

const applySelected = () => {
  const ids = Object.keys(catalogTableRef.value?.getSelectedRows() ?? []);
  ids.forEach((rowId) => {
    let itemIndex = selectedVariants.value?.findIndex(
      (item) => item.id === Number(rowId)
    );
    if (itemIndex !== -1 && itemIndex !== undefined) {
      const bulkData = bulkOperationsRef.value?.bulkOperationsData;
      const selectedData = selectedVariants.value[itemIndex];
      const fieldsToUpdate = [
        "listPrice",
        "salePrice",
        "stock",
        "duration",
        "vatRate",
      ];

      fieldsToUpdate.forEach((field) => {
        const bulkValue = bulkData?.[field];
        const selectedValue = selectedData?.[field];

        if (
          bulkValue !== undefined &&
          bulkValue !== selectedValue &&
          bulkValue !== 0 &&
          bulkValue !== "" &&
          bulkValue !== null
        ) {
          catalogTableRef.value?.table?.options?.meta.updateRow(
            Number(rowId),
            field,
            bulkValue
          );
        }
      });
    }
  });

  catalogTableRef.value.table.resetRowSelection();
};

const goToProductPage = () => {
  const currentHostname = window.location.hostname;
  const baseUrl = HOST_NAMES[currentHostname] ?? "https://www.n11.com";

  const catalogUrl = `${baseUrl}/urun/catalog-`;
  const url = `${catalogUrl}${selectedProduct.value?.productGroupId}`;

  window.open(url, "_blank");
};

const createEmptyValidation = () => ({
  isDirty: false,
  error: false,
  message: null,
});

const createEmptyVariant = (pimsId, gtin, categoryId) => ({
  id: pimsId,
  pimsId,
  name: "",
  gtin,
  categoryId,
  stockCode: null,
  listPrice: null,
  salePrice: null,
  stock: null,
  duration: null,
  validations: {
    stockCode: createEmptyValidation(),
    listPrice: createEmptyValidation(),
    salePrice: createEmptyValidation(),
    stock: createEmptyValidation(),
    duration: createEmptyValidation(),
    vatRate: createEmptyValidation(),
  },
});

const handleNoVariants = async (productList) => {
  hasClickedProductVariants.value = false;
  const firstProduct = productList?.[0];
  isSkuExist.value = firstProduct.isSkuExist;
  if (firstProduct) {
    const emptyVariant = createEmptyVariant(
      firstProduct.pimsId,
      firstProduct.gtin,
      firstProduct.categoryId
    );
    selectedVariants.value = [emptyVariant];
  }
};

const handleWithVariants = async (productList) => {
  isSkuExist.value = false;
  const isSkuExistValue = productList.find(
    (product) => product.pimsId === selectedProduct.value?.pimsId
  )?.isSkuExist;
  await Promise.resolve();
  if (
    addCatalogModalSelectableAreaRef.value &&
    selectedProduct.value?.pimsId &&
    !isSkuExistValue
  ) {
    addCatalogModalSelectableAreaRef.value.addSelectedItemsValue(
      selectedProduct.value.pimsId
    );
  }
};

const getClickedProductInfo = async (pimsId, groupId, item) => {
  emit("loadingClickedProductId", pimsId);
  selectedProductCategoryId.value = item.categoryId;
  store.commit("categories/SET_CATEGORY_ID", item.categoryId);

  try {
    const res = await store.dispatch("products/getProductInfo", { groupId });
    const transformedVariantList =
      res?.variantList?.map((variant) => ({
        ...variant,
        name: variant.value || variant.name || "",
        value: variant.value || variant.name || "",
      })) || [];

    clickedProductModalData.value = {
      ...res,
      variantList: transformedVariantList,
    };
    catalogModalStyle.value = { display: "block" };

    const hasVariants = transformedVariantList.length > 0;
    const { productList } = res;

    const productListWithCategory = productList.map((product) => ({
      ...product,
      categoryId: item.categoryId,
    }));

    if (hasVariants) {
      await handleWithVariants(productListWithCategory);
    } else {
      await handleNoVariants(productListWithCategory);
    }
  } catch (error) {
    console.error(error);
    commonModalRef.value?.hiddenModal();
  } finally {
    emit("loadingClickedProductId", false);
  }
};

const imageUrl =
  "https://n11scdn.akamaized.net/custom/upload/64/53/3650777660751629915.svg";
const getClickedProductModalImages = async (item, pimsId, groupId) => {
  try {
    const { data } = await store.dispatch("products/getProductImages", pimsId);
    clickedProductModalImages.value = data?.length ? data : [{ imageUrl }];
  } catch (error) {
    console.error("Ürün resimleri yüklenirken hata:", error);
    clickedProductModalImages.value = [{ imageUrl }];
  } finally {
    selectedProduct.value = {
      ...item,
      attributes: [
        { Key: "Kategori", Value: item.categoryName ?? null },
        { Key: "Marka", Value: item.brand ?? null },
        { Key: "Renk", Value: item.color ?? null },
      ],
      commission: item.commissionRateValue ?? null,
    };
    commonModalRef.value?.openModal();
    catalogModalStyle.value = { display: "none" };
  }
  await getClickedProductInfo(pimsId, groupId, item);
};

const isOpen = async (item) => {
  isVariantAddSuccess.value = false;
  await getClickedProductModalImages(item, item.pimsId, item.productGroupId);
  if (isSkuExist.value) {
    pimsId.value = selectedProduct.value?.pimsId;
    emit("openListedProductWarningModal", pimsId);
    commonModalRef.value?.hiddenModal();
  }
};

const clickedOpenSaleProductBtn = async () => {
  isClickOpenSaleProductBtn.value = true;
  isOpenSaleProductBtnDisabled.value = true;

  let isValid = false;

  const getTableData = catalogTableRef.value?.table?.options?.meta?.getData();
  isValid = !!getTableData.length;

  if (isValid) {
    isValid &= !!selectedShipmentTemplate.value?.id;
    getTableData.map((field) => {
      const valid = Object.keys(field.validations).map((fieldName) => {
        const validated = validate(
          field[fieldName],
          fieldName,
          field,
          true,
          catalogTableRef.value?.table?.options?.meta?.getColumnDataExceptThis(
            fieldName,
            field.id
          )
        );

        return validated;
      });

      isValid &= valid.every((val) => val === true);
    });
  }

  if (isValid) {
    const sanitizedTableData = JSON.parse(JSON.stringify(getTableData));

    const productDetails = sanitizedTableData.map((b) => {

      b.preparingTime = b.duration;
      b.vatRate = Number(b.vatRate);

      delete b.id;
      delete b.duration;
      delete b.validations;
      delete b.isCustom;
      delete b.disabled;
      delete b.imgUrl;

      return b;
    });

    try {
      const res = await store.dispatch("products/openForSale", {
        productGroupId: selectedProduct?.value?.productGroupId,
        shipmentTemplate: selectedShipmentTemplate.value.name,
        productDetails,
      });

      let successCount = res.data.successCount;
      if (successCount > 0) {
        sendOpenProductForSaleEvent(successCount);
      }

      if (res.data.failCount === 0) {
        sendProductDetailEvent(GA4_EVENT.ADD_VARIANT_SUCCESS, {
          button_name: 'Ürünü Satışa Aç',
          button_type: 'Put up for Sale',
          button_area: 'Order and Variant Information',
          item_id: String(props.productId || ''),
        });

        isVariantAddSuccess.value = true;
        commonModalRef.value.hiddenModal();

        emit("setIsNotificationShow", true);
        setTimeout(() => {
          emit("setIsNotificationShow", false);
        }, 10000);

        if (clickedProductModalImages.value && clickedProductModalImages.value.length > 0) {
          const imageUrl = clickedProductModalImages.value[0]?.imageUrl;
          if (imageUrl) {
            successProcessProductImg.value = imageUrl;
          }
        }
      } else {
        store.commit("common/SET_TOASTER_DATA", {
          type: "error",
          title: "",
          description:
            res.data.errorDetails[Object.keys(res.data.errorDetails)[0]],
        });
      }
    } catch (err) {
      store.commit("common/SET_TOASTER_DATA", {
        type: "error",
        title: "",
        description: err.errorDetails[Object.keys(err.errorDetails)[0]],
      });
    } finally {
      isOpenSaleProductBtnDisabled.value = false;
    }
  } else {
    setTimeout(() => {
      isOpenSaleProductBtnDisabled.value = false;
    }, 150);
  }
};

const addProductModalClosed = () => {
  if (!isVariantAddSuccess.value) {
    isVariantAddSuccess.value = true;
    sendProductDetailEvent(GA4_EVENT.ADD_VARIANT_DISMISS, {
      button_name: 'Geri Dön',
      button_type: 'Go Back',
      button_area: 'Order and Variant Information',
      item_id: String(props.productId || ''),
    });
  }

  selectedVariants.value = [];
  selectedShipmentTemplate.value = {};
  hasClickedProductVariants.value = true;
  isClickOpenSaleProductBtn.value = false;
  catalogModalStyle.value = { display: "none" };

  if (bulkOperationsRef && bulkOperationsRef.value) {
    bulkOperationsRef.value.bulkOperationsData = {
      listPrice: null,
      salePrice: null,
      stock: null,
      duration: null,
    };
  }
};

const handleSelectedVariants = (variants) => {
  selectedVariants.value = variants.map((variant) => {
    const productInfo = clickedProductModalData.value?.productList?.find(
      (p) => p.pimsId === variant.id
    );

    if (productInfo) {
      return {
        ...variant,
        pimsId: productInfo.pimsId || variant.id,
        gtin: productInfo.gtin || variant.gtin,
        categoryId: productInfo.categoryId || variant.categoryId || selectedProductCategoryId.value,
      };
    }

    return variant;
  });
};

watch(getCatalogModalDeleteRowId, (newDeleteRowId) => {
  if (newDeleteRowId) {
    catalogTableRef.value?.table?.options?.meta.removeRow(newDeleteRowId);
  }
});

const closeModal = () => {
  commonModalRef.value?.hiddenModal?.();
};

const closeModalWithoutDismiss = () => {
  isVariantAddSuccess.value = true;
  commonModalRef.value?.hiddenModal?.();
};

defineExpose({
  isOpen,
  successProcessProductImg,
  openModal: () => commonModalRef.value?.openModal?.(),
  hiddenModal: () => commonModalRef.value?.hiddenModal?.(),
  closeModal,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.catalog-product-modal {
  display: flex;
  gap: 16px;
  color: $colorN80;
  text-align: left;

  .catalog-product-main-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 64px;
  }

  .modal-product-title {
    display: inline-block;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.02px;
    line-height: 26px;
    margin-bottom: 24px;
    cursor: pointer;
    text-decoration: underline;
  }

  .modal-bottom {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    position: absolute;
    bottom: 0;
    width: 100%;

    .modal-bottom-alert {
      font-size: 13px;
      letter-spacing: -0.05px;
      width: 100%;
      padding: 12px 16px;
      display: flex;
      align-items: center;
      border-radius: 6px;
      gap: 12px;
      background-color: $colorB05;

      img {
        width: 24px;
        height: 24px;
      }
    }

    .modal-bottom-text {
      b {
        font-weight: 700;
      }
    }
  }
}
</style>
